// TradingViewWidget.tsx
// Based on official TradingView documentation: https://www.tradingview.com/widget-docs/widgets/charts/advanced-chart/
// Note: Only BSE (Bombay Stock Exchange) is available for free widget embedding. NSE is not supported.

import React, { useEffect, useRef, memo } from 'react';

interface TradingViewWidgetProps {
  symbol: string;           // Stock ticker (e.g., "RELIANCE", "TCS")
  theme?: 'light' | 'dark';
}

const TradingViewWidget: React.FC<TradingViewWidgetProps> = ({
  symbol,
  theme = 'light',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scriptAddedRef = useRef<boolean>(false);

  // Always use BSE for TradingView widgets since NSE is not supported
  // TradingView widgets only support BSE for Indian stocks (EOD data)
  const tradingViewSymbol = `BSE:${symbol}`;

  useEffect(() => {
    // Prevent adding script multiple times
    if (scriptAddedRef.current) return;
    if (!containerRef.current) return;

    const script = document.createElement('script');
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.type = 'text/javascript';
    script.async = true;
    script.innerHTML = `
      {
        "autosize": true,
        "symbol": "${tradingViewSymbol}",
        "interval": "W",
        "timezone": "Asia/Kolkata",
        "theme": "${theme}",
        "style": "1",
        "locale": "en",
        "allow_symbol_change": true,
        "calendar": false,
        "hide_side_toolbar": false,
        "hide_top_toolbar": false,
        "hide_legend": false,
        "save_image": true,
        "withdateranges": true,
        "support_host": "https://www.tradingview.com"
      }`;

    containerRef.current.appendChild(script);
    scriptAddedRef.current = true;

    // No cleanup - TradingView widget manages itself
  }, [tradingViewSymbol, theme]);

  return (
    <div 
      className="tradingview-widget-container" 
      ref={containerRef} 
      style={{ height: '100%', width: '100%' }}
    >
      <div 
        className="tradingview-widget-container__widget" 
        style={{ height: 'calc(100% - 32px)', width: '100%' }}
      />
      <div className="tradingview-widget-copyright">
        <a 
          href={`https://www.tradingview.com/symbols/${tradingViewSymbol}/`} 
          rel="noopener nofollow" 
          target="_blank"
        >
          <span className="blue-text">{symbol} stock chart</span>
        </a>
        <span className="trademark"> by TradingView</span>
        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#9db2bd' }}>(BSE data - EOD)</span>
      </div>
    </div>
  );
};

export default memo(TradingViewWidget);
